import React, { useContext, useState, useRef, useEffect } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import { useLogger } from '../../contexts/Logger';
import { useWithdrawalVerification, getWithdrawalError, updateAfterWithdrawal } from '../../hooks/useWithdrawalVerification';
import { gql } from 'apollo-boost';
import { IdsButton } from '@investec/ids-web-react';
import { customerDataContext } from '../../contexts/CustomerDataProvider';
import { useUnifiedAnalytics } from '../../contexts/AnalyticsWrapper';
import { formatCurrency } from '../../utils/helpers';
import FormattedInput from '../Form/TextInput/FormattedInput';
import IsaTransferOtpModal from './IsaTransferOtpModal';
import { isEligibleIsaTransferDestination } from './isaTransferEligibility';

import './isaTransferToInvestec.scss';

const getAccountNumberSuffix = (accountNumber?: string): string =>
    accountNumber || 'XXXX';

const UPDATE_TRANSFER_SOURCE_ACCOUNT = gql`
    fragment UpdateTransferSourceAccount on AccountType {
        balance
        availableBalance
    }
`;

const UPDATE_TRANSFER_DESTINATION_ACCOUNT = gql`
    fragment UpdateTransferDestinationAccount on AccountType {
        balance
    }
`;

interface IsaAccount {
    accountNumber?: string;
    accountStatus?: string;
    balance?: number;
    availableBalance?: number;
    id: string;
    sortCode?: string;
    product?: {
        name?: string;
        productType?: string;
        stepUpAmount?: number;
        maxWithdrawalLimit?: number;
    };
}

interface IsaTransferToInvestecStepProps {
    accountId: string;
    onBack: () => void;
    onOtpVerified: () => void;
}

const IsaTransferToInvestecStep: React.FC<IsaTransferToInvestecStepProps> = ({
    accountId,
    onBack,
    onOtpVerified
}) => {
    const { data: customerData } = useContext(customerDataContext) ?? {};
    const accounts: IsaAccount[] = customerData?.customer?.accounts ?? [];
    const sourceAccount = accounts.find(account => account.id === accountId);
    const otherIsas = accounts.filter(account =>
        account.id !== accountId && isEligibleIsaTransferDestination(account)
    );
    const sourceBalance = sourceAccount ? formatCurrency(sourceAccount.balance) : '';
    const sourceAccountName = sourceAccount?.product?.name ?? 'Cash ISA';
    const sourceAccountNumberSuffix = getAccountNumberSuffix(sourceAccount?.accountNumber);
    const [selectedAccount, setSelectedAccount] = useState<IsaAccount | null>(null);
    const [transferAmount, setTransferAmount] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
    const [verificationToken, setVerificationToken] = useState('');
    const [isOtpLoading, setIsOtpLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [otpError, setOtpError] = useState('');
    const { sendStepUpOtp, submitWithdrawal } = useWithdrawalVerification('IsaTransferToInvestec');
    const logger = useLogger();
    const paymentAccepted = useRef(false);
    const apolloClient = useApolloClient();
    const analytics = useUnifiedAnalytics();

    useEffect(() => {
        if (analytics) {
            analytics.trackAnalyticsPageEvent(
                'ISA Transfer to Investec page loaded',
                {},
                'ISA Transfer',
                'ISA_Transfer_To_Investec|Page_Load|To_Investec_View'
            );
        }
    }, [analytics]);

    const transferAmountValue = Number(transferAmount.replace(/[^\d.]/g, ''));
    const sourceBalanceValue = sourceAccount?.availableBalance ?? 0;
    const maxWithdrawalLimit = sourceAccount?.product?.maxWithdrawalLimit as number | undefined;

    const isAmountMoreThanBalance = (): boolean => {
        return Number((sourceBalanceValue - transferAmountValue).toFixed(2)) < 0;
    };

    const isAmountMoreThanWithdrawalLimit = (): boolean => {
        return !!(transferAmountValue && maxWithdrawalLimit && transferAmountValue > maxWithdrawalLimit);
    };

    const isValidAmount = (): boolean => {
        return transferAmountValue > 0 && !isAmountMoreThanBalance() && !isAmountMoreThanWithdrawalLimit();
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const renderAmountError = (): React.ReactNode | null => {
        if (!transferAmount) return null;

        if (isAmountMoreThanBalance()) {
            return `You have up to ${formatCurrency(sourceBalanceValue)} available to transfer`;
        }

        if (isAmountMoreThanWithdrawalLimit()) {
            return `Max of ${formatCurrency(maxWithdrawalLimit)} allowed for each transfer`;
        }

        return null;
    };

    const amountError = renderAmountError();

    const shouldStepUp = Number.isFinite(transferAmountValue) &&
        transferAmountValue > 0 &&
        transferAmountValue >= (sourceAccount?.product?.stepUpAmount ?? 0);

    const openOtpModal = async () => {
        if (isOtpLoading) return;
        setVerificationToken('');
        setIsOtpModalOpen(true);
        setOtpError('');
        setIsOtpLoading(true);

        try {
            const token = await sendStepUpOtp();

            if (token) {
                setVerificationToken(token);
                if (analytics) {
                    analytics.trackAnalyticsClickEvent(
                        'OTP sent for ISA transfer to Investec',
                        '',
                        'modal',
                        'ISA Transfer',
                        '',
                        {},
                        'click',
                        'ISA_Transfer_To_Investec|OTP|Sent',
                        'ISA Transfer'
                    );
                }
            }
        } catch (e) {
            setOtpError(getWithdrawalError(e).message);
        } finally {
            setIsOtpLoading(false);
        }
    };

    const submitTransfer = async (otp?: string, otpMode = 'sms') => {
        if (!sourceAccount || !selectedAccount || paymentAccepted.current || isSubmitting || !isValidAmount()) {
            return { success: false, errorType: 'unexpected' };
        }

        setIsSubmitting(true);
        setOtpError('');

        let withdrawal;
        try {
            withdrawal = await submitWithdrawal({
                amount: transferAmountValue,
                id: accountId,
                previousBalance: sourceAccount.balance ?? 0,
                destinationSortCode: selectedAccount.sortCode ?? '',
                destinationAccountNumber: selectedAccount.accountNumber ?? '',
                reference: `Transfer to ${selectedAccount.product?.name ?? 'Cash ISA'}`,
                verifyOtp: otp ? {
                    mode: otpMode,
                    passCode: otp,
                    verificationToken
                } : null
            }, sourceAccount.availableBalance);
        } catch (e) {
            const error = getWithdrawalError(e);
            setIsSubmitting(false);
            return { success: false, errorType: error.type };
        }

        paymentAccepted.current = true;
        setIsOtpModalOpen(false);
        updateAfterWithdrawal(() => {
            const responseBalance = Number(withdrawal.updatedBalance);
            const responseAvailableBalance = Number(withdrawal.availableBalance);
            const updatedBalance = Number.isFinite(responseBalance)
                ? responseBalance
                : (sourceAccount.balance ?? 0) - transferAmountValue;
            const updatedAvailableBalance = Number.isFinite(responseAvailableBalance)
                ? responseAvailableBalance
                : (sourceAccount.availableBalance ?? sourceAccount.balance ?? 0) - transferAmountValue;

            sourceAccount.balance = updatedBalance;
            sourceAccount.availableBalance = updatedAvailableBalance;
            apolloClient.writeFragment({
                id: `AccountType:${sourceAccount.id}`,
                fragment: UPDATE_TRANSFER_SOURCE_ACCOUNT,
                data: {
                    __typename: 'AccountType',
                    balance: updatedBalance,
                    availableBalance: updatedAvailableBalance
                }
            });

            const destinationBalance = (selectedAccount.balance ?? 0) + transferAmountValue;
            selectedAccount.balance = destinationBalance;
            apolloClient.writeFragment({
                id: `AccountType:${selectedAccount.id}`,
                fragment: UPDATE_TRANSFER_DESTINATION_ACCOUNT,
                data: {
                    __typename: 'AccountType',
                    balance: destinationBalance
                }
            });
        }, () => logger?.error("Payment accepted but account display update failed"));

        updateAfterWithdrawal(() => {
            if (analytics) {
                analytics.trackAnalyticsClickTransactionEvent(
                    'submit',
                    'ISA Transfer',
                    'transfer',
                    selectedAccount?.product?.name ?? 'Cash ISA',
                    'ISA_Transfer_To_Investec|Transaction|Transfer_Submitted',
                    'ISA Transfer'
                );
            }

        }, () => logger?.error("Payment accepted but analytics update failed"));
        updateAfterWithdrawal(onOtpVerified, () => logger?.error("Payment accepted but confirmation display failed"));
        setIsSubmitting(false);
        return { success: true };

    };

    const continueTransfer = async () => {
        if (isOtpLoading || isSubmitting || paymentAccepted.current) return;
        if (shouldStepUp) {
            await openOtpModal();
            return;
        }

        await submitTransfer();
    };

    return (
        <main className="isa-transfer-to-investec">
            <h1>Transfer to another Investec Save cash ISA</h1>
            <p className="isa-transfer-to-investec__description">
                Choose the Investec Save cash ISA you&apos;d like to transfer your money to, and the
                amount you&apos;d like to transfer. Your money will keep its tax-free status. If you want to transfer to a new Fixed Rate Cash ISA, you can open a new one from your dashboard.
            </p>

            <div className="isa-transfer-to-investec__fields">
                <div className="isa-transfer-to-investec__field-group">
                    <label>From account</label>
                    <div className="isa-transfer-to-investec__field-disabled">
                        <span>{sourceAccountName} ({sourceAccountNumberSuffix})</span>
                    </div>
                </div>
                <div className="isa-transfer-to-investec__field-group">
                    <label>Balance</label>
                    <div className="isa-transfer-to-investec__field-disabled">
                        <span>{sourceBalance}</span>
                    </div>
                </div>
                <div className="isa-transfer-to-investec__field-group">
                    <label>To account</label>
                    <div className="isa-transfer-to-investec__field-dropdown" ref={dropdownRef}>
                        <div
                            className="isa-transfer-to-investec__dropdown-trigger"
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            role="combobox"
                            aria-expanded={isDropdownOpen}
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsDropdownOpen(!isDropdownOpen); } }}
                        >
                            <span className={selectedAccount ? 'isa-transfer-to-investec__dropdown-selected' : 'isa-transfer-to-investec__dropdown-placeholder'}>
                                {selectedAccount
                                    ? `${selectedAccount.product?.name ?? 'Cash ISA'} (${selectedAccount.accountNumber ?? 'XXXX'})`
                                    : 'Select an account'}
                            </span>
                            <svg className="isa-transfer-to-investec__dropdown-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7 10L12 15L17 10" stroke="#30384A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        {isDropdownOpen && (
                            <div className="isa-transfer-to-investec__dropdown-menu">
                                {otherIsas.map(account => (
                                    <button
                                        key={account.id}
                                        className={`isa-transfer-to-investec__dropdown-item ${selectedAccount?.id === account.id ? 'isa-transfer-to-investec__dropdown-item--selected' : ''}`}
                                        onClick={() => {
                                            setSelectedAccount(account);
                                            setIsDropdownOpen(false);
                                            if (analytics) {
                                                analytics.trackAnalyticsClickEvent(
                                                    'Selected destination account',
                                                    '',
                                                    'dropdown',
                                                    'ISA Transfer',
                                                    account.id,
                                                    { accountName: account.product?.name ?? 'Cash ISA' },
                                                    'click',
                                                    'ISA_Transfer_To_Investec|Select|Destination_Account',
                                                    'ISA Transfer'
                                                );
                                            }
                                        }}
                                    >
                                        {account.product?.name ?? 'Cash ISA'} ({account.accountNumber ?? 'XXXX'})
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {selectedAccount?.product?.name === '1-Year Fixed Rate Cash ISA' && (
                        <p className="isa-transfer-to-investec__fixed-rate-warning">
                            Transferring into a Fixed Rate Cash ISA means you won&apos;t be able to access your savings until the term ends.
                        </p>
                    )}
                </div>
                <div className="isa-transfer-to-investec__field-group">
                    <label>Transfer amount</label>
                    <div className="isa-transfer-to-investec__field-input-wrapper">
                        <span className="isa-transfer-to-investec__currency-prefix">£</span>
                        <FormattedInput
                            type="text"
                            className="isa-transfer-to-investec__field-input"
                            value={transferAmount}
                            options={{ numeral: true, numeralThousandsGroupStyle: 'thousand', numeralPositiveOnly: true }}
                            onChange={setTransferAmount}
                            onBlur={() => {
                                if (transferAmount && Number.isFinite(transferAmountValue)) {
                                    setTransferAmount((Math.round(transferAmountValue * 100) / 100).toFixed(2));
                                }
                            }}
                            placeholder="Amount"
                        />
                    </div>
                    {amountError && (
                        <span className="isa-transfer-to-investec__error">{amountError}</span>
                    )}
                </div>
            </div>

            <div className="isa-transfer-to-investec__actions">
                <IdsButton appearance="default" size="md" disabled={isOtpLoading || isSubmitting} onClick={() => {
                    if (analytics) {
                        analytics.trackAnalyticsClickEvent(
                            'Back from ISA transfer to Investec',
                            '',
                            'button',
                            'ISA Transfer',
                            '',
                            {},
                            'click',
                            'ISA_Transfer_To_Investec|Button_Click|Back',
                            'ISA Transfer'
                        );
                    }
                    onBack();
                }}>
                    Back
                </IdsButton>
                <IdsButton
                    appearance="primary"
                    disabled={!selectedAccount || !isValidAmount() || isOtpLoading || isSubmitting || paymentAccepted.current}
                    size="md"
                    onClick={() => {
                        if (analytics) {
                            analytics.trackAnalyticsClickEvent(
                                'Continue on ISA transfer to Investec',
                                '',
                                'button',
                                'ISA Transfer',
                                '',
                                {},
                                'click',
                                'ISA_Transfer_To_Investec|Button_Click|Continue',
                                'ISA Transfer'
                            );
                        }
                        continueTransfer();
                    }}
                >
                    Continue
                </IdsButton>
            </div>
            <IsaTransferOtpModal
                isOpen={isOtpModalOpen}
                isLoading={isSubmitting}
                isSendingOtp={isOtpLoading}
                onRetrySend={openOtpModal}
                errorMessage={otpError}
                verificationToken={verificationToken}
                onClose={() => {
                    setIsOtpModalOpen(false);
                    setOtpError('');
                    if (analytics) {
                        analytics.trackAnalyticsClickEvent(
                            'OTP modal closed',
                            '',
                            'modal',
                            'ISA Transfer',
                            '',
                            {},
                            'click',
                            'ISA_Transfer_To_Investec|OTP|Close',
                            'ISA Transfer'
                        );
                    }
                }}
                onContinue={async (otp, otpMode) => {
                    if (analytics) {
                        analytics.trackAnalyticsClickEvent(
                            'OTP continue on ISA transfer to Investec',
                            '',
                            'modal',
                            'ISA Transfer',
                            '',
                            {},
                            'click',
                            'ISA_Transfer_To_Investec|OTP|Continue',
                            'ISA Transfer'
                        );
                    }
                    return submitTransfer(otp, otpMode);
                }}
            />
        </main>
    );
};

export default IsaTransferToInvestecStep;
